/**
 * Core storage system types and interfaces
 */
import type { WriteGuard, WriteRefusalReason } from "../documentWriteGuard";

/**
 * Base storage options used across all storage types
 */
export interface StorageOptions {
  // Operation metadata
  operation: {
    type: "read" | "write" | "delete";
    source: string;
    timestamp: number;
  };

  // Validation requirements
  validation?: {
    schema?: unknown; // Will be defined per data type
    maxSize?: number;
    allowedTypes?: string[];
  };

  // Logging configuration
  logging?: {
    level: "none" | "basic" | "detailed";
    includeMetadata?: boolean;
  };
}

/**
 * What a `project_*` document write declares (WR-685). The coordinator hands it to
 * `IndexedDBManager.guardedSave`, where `decideDocumentWrite` rules on it inside the
 * write's own transaction.
 */
export interface DocumentWriteOptions {
  /** owner / base revision / intent — see `WriteGuard` */
  guard?: WriteGuard;
  /**
   * The R-MANIFEST-EMPTY-SAVE pre-image journal, run INSIDE the guarded transaction and
   * only when the write is allowed: given the stored value at the key, return the value
   * to journal at `key` (or undefined for none). Must not throw into the transaction —
   * a throw is caught and journals nothing.
   */
  preimage?: { key: string; of: (stored: unknown) => unknown };
}

/**
 * Result of a storage operation
 */
export interface StorageResult<T> {
  success: boolean;
  data?: T;
  error?: StorageError;
  metadata?: {
    timestamp: number;
    operation: string;
    size?: number;
    /** guarded drawing write: the revision now stored */
    rev?: number;
    /** guarded write refused — nothing was written */
    refusal?: WriteRefusalReason;
    /** guarded write: the pre-image journaled in the same transaction, if any */
    preimage?: unknown;
  };
}

/**
 * Storage-specific error with additional context
 */
export class StorageError extends Error {
  constructor(
    message: string,
    public code: StorageErrorCode,
    public metadata?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "StorageError";
  }
}

/**
 * Error codes for storage operations
 */
export enum StorageErrorCode {
  // Availability errors
  NOT_AVAILABLE = "STORAGE_NOT_AVAILABLE",
  QUOTA_EXCEEDED = "STORAGE_QUOTA_EXCEEDED",

  // Operation errors
  INVALID_DATA = "INVALID_DATA",
  VALIDATION_FAILED = "VALIDATION_FAILED",
  OPERATION_FAILED = "OPERATION_FAILED",
  /** WR-685: the document write guard refused the write; nothing was written. Never retried. */
  WRITE_REFUSED = "WRITE_REFUSED",

  // State errors
  VERSION_MISMATCH = "VERSION_MISMATCH",
  CORRUPTED_DATA = "CORRUPTED_DATA",

  // System errors
  INTERNAL_ERROR = "INTERNAL_ERROR",
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
}

/**
 * Base interface for all storage implementations
 */
export interface StorageLayer {
  // Core operations
  save<T>(
    key: string,
    data: T,
    options?: StorageOptions,
  ): Promise<StorageResult<void>>;
  load<T>(key: string, options?: StorageOptions): Promise<StorageResult<T>>;
  delete(key: string, options?: StorageOptions): Promise<StorageResult<void>>;

  // Storage management
  clear(options?: StorageOptions): Promise<StorageResult<void>>;
  getStatus(): Promise<StorageStatus>;
}

/**
 * Extended storage status with detailed information
 */
export interface StorageStatus {
  // Basic status
  available: boolean;
  usedSpace: number;
  totalSpace: number;

  // Detailed status
  operations?: {
    pending: number;
    completed: number;
    failed: number;
  };

  // Health metrics
  health?: {
    errorRate: number;
    avgOperationTime: number;
    lastError?: StorageError;
  };

  // Error information
  errorMessage?: string;
}

/**
 * Storage validation result
 */
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

/**
 * Validation error details
 */
export interface ValidationError {
  code: string;
  message: string;
  path?: string[];
  metadata?: Record<string, unknown>;
}

/**
 * Operation log entry
 */
export interface OperationLog {
  timestamp: number;
  operation: string;
  key: string;
  status: "success" | "failure";
  duration: number;
  error?: StorageError;
  metadata?: Record<string, unknown>;
}

/**
 * Storage event for monitoring
 */
export interface StorageEvent {
  type: "operation" | "error" | "status";
  timestamp: number;
  data: unknown;
  metadata?: Record<string, unknown>;
}

/**
 * Storage event handler
 */
export type StorageEventHandler = (event: StorageEvent) => void;
